import type { z } from 'zod';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ReadOnlyArray } from '../../../utils/array/ReadOnlyArray';
import { NOT_VALID_FOR_STRUCURE_CATALOG } from '../../../utils/catalog/notvalidforstructure';
import { UNCATEGORIZED_CATALOG } from '../../../utils/catalog/uncategorized';
import { DELETE, GET, POST, PUT } from './api';
import { FETCH_ITEMS_PAGE_SIZE } from '../../../utils/data/constants';
import { compactItemNo } from '../../../utils/data/utils';
import { selectMarketProps } from '../../selectors/market/market';
import {
  AddItemsSchema,
  AddItemsToMultipleSchema,
  AutoCategorizedItemsReport,
  ItemDetails,
  ItemHistorySchema,
  ItemRelationsSchema,
  ItemsSchema,
  MarketSalesDateSchema,
  MissingItemsSchema,
  NotValidForStructureItemsSchema,
  UncategorizedItemsSchema,
} from '../../../schemas/items';
import type { Item } from '../../../entities/Item';
import type { RootState } from '../..';

export const getAutoCategorizedItems = createAsyncThunk(
  'item/autocategoried',
  async (
    {
      catalogId,
      from,
      to,
      source,
      pageNo,
    }: {
      catalogId: string;
      from: string;
      to: string;
      source: string;
      pageNo: string;
    },
    thunkAPI,
  ) => {
    const { dispatch } = thunkAPI;
    const pageSize = 20;
    const data = await dispatch(
      GET({
        path: `/rrm-rc/ZZ/en/${catalogId}/items/auto-categorised/${from}/${to}?pageNo=${pageNo}&source=${source}&pageSize=${pageSize}`,
        label: `Get auto categorized items`,
      }),
    );

    const parseData = AutoCategorizedItemsReport.parse(data);
    return parseData;
  },
);

export const addItemsToCategories = createAsyncThunk<
  z.infer<typeof AddItemsToMultipleSchema>,
  { catalogId: string; itemNos: string[]; categories: string[] },
  { state: RootState }
>(
  'item/addItemsToCategories',
  async ({ catalogId, itemNos, categories }, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;

    const { languageCode, retailUnit } = selectMarketProps(getState());
    const data = await dispatch(
      POST({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/${catalogId}/categorise/items`,
        data: {
          itemNos,
          categories,
        },
        label: `Add items ${itemNos.map((m: string) => m)} to categories ${categories.map((m: string) => m)}`,
      }),
    );

    return AddItemsToMultipleSchema.parse(data);
  },
);

export const addItemsToCategory = createAsyncThunk<
  z.infer<typeof AddItemsSchema>,
  { catalogId: string; categoryId: string; items: { itemNo: string }[] },
  { state: RootState }
>(
  'item/addItems',
  async (
    {
      catalogId,
      categoryId,
      items,
    }: { catalogId: string; categoryId: string; items: { itemNo: string }[] },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const payload = items.map((item) => ({ itemNo: item.itemNo }));
    const { languageCode, retailUnit } = selectMarketProps(getState());
    const data = await dispatch(
      POST({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/${catalogId}/categories/${categoryId}/items`,
        data: payload,
        label: `Add items to Category with id: ${categoryId} in Catalogue with id: ${catalogId}`,
      }),
    );

    return AddItemsSchema.parse(data);
  },
);

export const getItems = createAsyncThunk(
  'item/get',
  async (
    {
      catalogId,
      categoryId,
      pageNo = 0,
    }: { catalogId: string; categoryId: string; pageNo: number },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);

    const data = await dispatch(
      GET({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/${catalogId}/categories/${categoryId}/items?pageNo=${pageNo}&pageSize=${FETCH_ITEMS_PAGE_SIZE}`,
        label: `Get items page number: ${pageNo} for Category with id: ${categoryId} in Catalogue with id: ${catalogId}`,
      }),
    );
    const parsedData = ItemsSchema.parse(data);

    return {
      catalogId,
      categoryId,
      items: parsedData.content,
      paging: {
        lastPage: parsedData.last,
        pageNo: parsedData.pageable.pageNumber,
      },
      meta: {
        totalNumberOfElements: parsedData.totalElements,
      },
    };
  },
);

export const getItem = createAsyncThunk(
  'item/getItem',
  async (
    {
      itemNo,
      itemType,
      ignoreError = false,
      catalogId,
      onFailure = null,
    }: {
      itemNo: string;
      itemType: string;
      ignoreError?: boolean;
      catalogId: string;
      onFailure?: (() => void) | null;
    },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);
    const itemDto = await dispatch(
      GET({
        ignoreError,
        onFailure,
        path: `/rrm-rc/${retailUnit}/${languageCode}/items/${itemNo}/${itemType}?catalogue-id=${catalogId}`,
        label: `Get item with type: ${itemType} and item number: ${itemNo}`,
      }),
    );

    return ItemDetails.parse(itemDto);
  },
);

export const updateItem = createAsyncThunk(
  'item/update',
  async (
    {
      itemNo,
      catalogId,
      mainParentCategoryId,
    }: {
      itemNo: string;
      catalogId: string;
      mainParentCategoryId: string;
    },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);

    const data = await dispatch(
      PUT({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/${catalogId}/items/${itemNo}/main-parent/${mainParentCategoryId}`,
      }),
    );

    return data;
  },
);

export const moveItems = createAsyncThunk(
  'item/moveItems',
  async (
    {
      catalogId,
      categoryId,
      fromCategoryId,
      items,
    }: {
      catalogId: string;
      categoryId: string;
      fromCategoryId: string;
      items: Item[];
    },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const itemsList = items.map((item) => ({
      itemNo: item.itemNo,
      itemType: item.itemType,
    }));
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);
    const data = await dispatch(
      POST({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/${catalogId}/categories/${categoryId}/move-items`,
        data: { fromCategoryId, itemsList },
        label: `Move items from Category: ${fromCategoryId} to Category: ${categoryId} in Catalogue with id: ${catalogId}`,
      }),
    );
    const parsedData = AddItemsSchema.parse(data);
    return parsedData;
  },
);

export const removeItems = createAsyncThunk(
  'item/removeItems',
  async (
    {
      catalogId,
      categoryId,
      items,
    }: {
      catalogId: string;
      categoryId: string;
      items: Item[];
    },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const payload = items.map((item) => ({
      itemNo: item.itemNo,
      itemType: item.itemType,
    }));
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);
    return dispatch(
      DELETE({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/${catalogId}/categories/${categoryId}/items`,
        data: payload,
        label: `Remove items from Category with id: ${categoryId} in Catalogue with id: ${catalogId}`,
      }),
    );
  },
);

export const getItemHistory = createAsyncThunk<
  z.infer<typeof ItemHistorySchema>,
  { itemNo: string; itemType: string; pageNo: number },
  { state: RootState }
>(
  'item/itemHistory',
  async (
    {
      itemNo,
      itemType,
      pageNo,
    }: { itemNo: string; itemType: string; pageNo: number },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState());
    const historyEvents = await dispatch(
      GET({
        path: `/rrm-rc/${retailUnit}/${languageCode}/items/${itemNo}/${itemType}/events?pageNo=${pageNo}`,
        label: `Fetch history for item: ${itemNo}`,
      }),
    );
    return ItemHistorySchema.parse(historyEvents);
  },
);

export const getMissingItems = createAsyncThunk(
  'item/getMissingItems',
  async (_, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);
    const data = await dispatch(
      GET({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/items-missing-in-draft`,
        label: 'Get missing items',
      }),
    );
    const parsedData = MissingItemsSchema.parse(data);
    const { categories = [], items = [], topCategories = [] } = parsedData || {};

    const catalogCategoryFromDto = Object.fromEntries(
      categories?.map((cat) => [cat.categoryId, cat]) || [],
    );

    return {
      categoriesMapped: catalogCategoryFromDto,
      categories,
      items,
      languageCode,
      topCategories,
    };
  },
);

export const getNotValidForStructureItems = createAsyncThunk(
  'item/getNotValidForStructureItems',
  async ({ pageNo }: { pageNo: number }, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);
    const resultDto = await dispatch(
      GET({
        path: `/rrm-rc/${retailUnit}/${languageCode}/not-valid-for-structure-items?pageNo=${
          pageNo || 0
        }&pageSize=${999999999}`,
        label: `Get page number ${pageNo} of not valid for structure items`,
      }),
    );
    const parsedData = NotValidForStructureItemsSchema.parse(resultDto);

    return parsedData;
  },
);

export const getUncategorizedItems = createAsyncThunk(
  'item/getUncategorizedItems',
  async (_, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);
    const resultDto = await dispatch(
      GET({
        path: `/rrm-rc/${retailUnit}/${languageCode}/uncategorised-items`,
        label: `Get list of uncategorised items`,
      }),
    );
    return UncategorizedItemsSchema.parse(resultDto);
  },
);

export const getCategoryRelationsItem = createAsyncThunk(
  'item/getCategoryRelationsItem',
  async (
    { catalogId, itemNo }: { catalogId?: string; itemNo: string },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);

    const queryParam = catalogId ? `?catalogue-id=${catalogId}` : '';
    const data = await dispatch(
      GET({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/items/${compactItemNo(
          itemNo,
        )}${queryParam}`,
        label: `Get catalogue relations for item number ${itemNo}`,
      }),
    );

    const {
      notValidForStructure,
      uncategorised,
      categoryWithCountrySpecificItemList,
      catalogueList,
      structureReasonText,
      ...rest
    } = ItemRelationsSchema.parse(data);

    if (notValidForStructure) {
      catalogueList.push(NOT_VALID_FOR_STRUCURE_CATALOG.catalogId);
    }
    if (uncategorised) {
      catalogueList.push(UNCATEGORIZED_CATALOG.catalogId);
    }
    if (
      (!Array.isArray(categoryWithCountrySpecificItemList) ||
        !categoryWithCountrySpecificItemList.length) &&
      (!Array.isArray(catalogueList) || !catalogueList.length)
    ) {
      return {
        catalogIdsList: ReadOnlyArray.Empty,
        categoryIdsList: ReadOnlyArray.Empty,
        item: null,
      };
    }

    return {
      uncategorised,
      notValidForStructure,
      catalogIdsList: catalogueList,
      categoryWithCountrySpecificItemList,
      item: rest,
      structureReasonText,
    };
  },
);

export const getMarketSalesDates = createAsyncThunk(
  'item/marketSalesDate',
  async ({ itemNo, itemType }: { itemNo: string; itemType?: string }, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const data = await dispatch(
      GET({
        path: `/rrm-rc/ZZ/en/item/${itemNo}/${itemType}/market-sales-dates`,
        label: `Get market sales date for  ${itemNo}-${itemType}`,
      }),
    );

    const parsedData = MarketSalesDateSchema.parse(data);
    return parsedData;
  },
);

interface PreviewItemsInput {
  catalogId: string;
  categoryId: string;
  itemNumbers: Item[];
}

export const previewItems = createAsyncThunk(
  'importitems/previewItems',
  async ({ catalogId, categoryId, itemNumbers }: PreviewItemsInput, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);
    const result = await dispatch(
      POST({
        path: `/rrm-rc/${retailUnit}/${languageCode}/catalogues/${catalogId}/categories/${categoryId}/preview-import-items`,
        data: itemNumbers.map((itemNo) => ({ itemNo })),
        label: `Preview items for category ${categoryId} in catalogue ${catalogId}`,
      }),
    );
    return result;
  },
);

export const getItemCategorizedStatistics = createAsyncThunk(
  'importitems/previewItems',
  async (
    {
      catalogId,
      from,
      to,
      source,
    }: {
      catalogId: string;
      from: string;
      to: string;
      source: string | undefined;
    },
    thunkAPI,
  ) => {
    const { dispatch, getState } = thunkAPI;
    const { languageCode, retailUnit } = selectMarketProps(getState() as RootState);
    const result = await dispatch(
      GET({
        path: `/rrm-rc/${retailUnit}/${languageCode}/${catalogId}/stats/item-categorised/${from}/${to}?source=${source}`,
        label: `Items categorised stats for catalogue ${catalogId} with date ${from} - ${to}`,
      }),
    );
    return result;
  },
);
